import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { partnerApi } from "../../../modules/partner/api";
import {
  CreateMultiplePartnerConfigs,
  LogoutFromPartnerSso,
  RequestPartnerSso,
  RequestPartnerSsoAuthRedirectUrl,
  UpdateMultiplePartnerConfigs,
} from "../../../modules/partner/types";
import type { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const PARTNER_QUERY_KEYS = {
  getPartnerConfigsAndSsoProperties: () => ["getPartnerConfigsAndSsoProperties"],
  getPartnerConfigsForSpecificClient: (id: UUID) => ["getPartnerConfigsForSpecificClient", id],
  createPartnerConfigs: () => ["createPartnerConfigs"],
  updatePartnerConfigs: () => ["updatePartnerConfigs"],
  deletePartnerConfigs: () => ["deletePartnerConfigs"],
  updatePartnerConfigBackgroundPicture: () => ["updatePartnerConfigBackgroundPicture"],
  deletePartnerConfigBackgroundPicture: () => ["deletePartnerConfigBackgroundPicture"],
  getPartnerSso: () => ["getPartnerSso"],
  logoutFromPartnerSso: () => ["logoutFromPartnerSso"],
  monlyAuthorizationCodeGrant: () => ["monlyAuthorizationCodeGrant"],
  getPartnerSsoAuthRedirectUrl: (payload: RequestPartnerSsoAuthRedirectUrl) => [
    "getPartnerSsoAuthRedirectUrl",
    payload,
  ],
};

export const useGetPartnerConfigsAndSsoProperties = (enabled?: boolean) => {
  return useQuery({
    queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await partnerApi.getPartnerConfigsAndSsoProps();

      return data;
    },
    enabled,
  });
};

export const useGetPartnerForSpecificClient = (id: UUID, enabled?: boolean) => {
  return useQuery({
    queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsForSpecificClient(id),
    queryFn: async () => {
      const { data } = await partnerApi.getPartnerConfigsForSpecificClient({ id });

      return data;
    },
    enabled,
  });
};

export const useCreatePartnerConfigs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.createPartnerConfigs(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateMultiplePartnerConfigs) => {
      const { data } = await partnerApi.createPartnerConfigs(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties() });
    },
  });
};

export const useUpdatePartnerConfigs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.updatePartnerConfigs(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: UpdateMultiplePartnerConfigs) => {
      const { data } = await partnerApi.updatePartnerConfigs(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties() });
    },
  });
};

export const useDeletePartnerConfigs = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.deletePartnerConfigs(),
    meta: { globalSpinner },
    mutationFn: (payload: UUID[]) => {
      return partnerApi.deletePartnerConfigs(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties() });
    },
  });
};

export const useUpdatePartnerConfigBackgroundPicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.updatePartnerConfigBackgroundPicture(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await partnerApi.updatePartnerConfigBackgroundPicture(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties() });
    },
  });
};

export const useDeletePartnerConfigBackgroundPicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.deletePartnerConfigBackgroundPicture(),
    mutationFn: (id: UUID) => {
      return partnerApi.deletePartnerConfigBackgroundPicture({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_QUERY_KEYS.getPartnerConfigsAndSsoProperties() });
    },
  });
};

export const useGetPartnerSso = () => {
  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.getPartnerSso(),
    mutationFn: async (payload: RequestPartnerSso) => {
      const { data } = await partnerApi.getPartnerSso(payload);

      return data;
    },
  });
};

export const useLogoutFromPartnerSso = () => {
  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.logoutFromPartnerSso(),
    mutationFn: async (payload: LogoutFromPartnerSso) => {
      const { data } = await partnerApi.logoutFromPartnerSso(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("partnerSso.helpers.logoutSuccessful", {
          ssoTypes: mutatedPayload.ssoTypes.map((ssoType) => t("partnerSso.enums.ssoType." + ssoType)).join(", "),
        }),
      });
    },
  });
};

export const useMonlyAuthorizationCodeGrant = () => {
  return useMutation({
    mutationKey: PARTNER_QUERY_KEYS.monlyAuthorizationCodeGrant(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: string) => {
      const { data } = await partnerApi.monlyAuthorizationCodeGrant(payload);

      return data;
    },
  });
};

export const useGetPartnerSsoAuthRedirectUrl = (payload: RequestPartnerSsoAuthRedirectUrl, enabled?: boolean) => {
  return useQuery({
    queryKey: PARTNER_QUERY_KEYS.getPartnerSsoAuthRedirectUrl(payload),
    queryFn: async () => {
      const { data } = await partnerApi.getAuthRedirectUrl(payload);

      return data;
    },
    enabled,
  });
};
